import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Grid } from "semantic-ui-react";
import {
  getDateFormats,
  getTimeFormats
} from "../../../../actions/dateTimeFormats/dateTimeFormats";
import {
  getRegionSettings,
  updateRegionSettings
} from "../../../../actions/regions/regionSettings";
import {
  getRegionSettingsPerUser,
  updateRegionSettingsPerUser
} from "../../../../actions/regions/regionSettingsPerUser";
import { canPerformAction, getUserSettings } from "../../../../actions/users/users";
import { AppDispatch, RootState } from "../../../../reducers";
import { getBasemaps } from "../../../../store/basemaps";
import { getMeasurementSystems } from "../../../../store/measurementSystems";
import { Role } from "../../../../types/roles";
import { exists } from "../../../../utils/utils";
import Settings from "../../../settings/Settings";
import Administrator from "./Administrator";
import User from "./User";

const mapStateToProps = ({
  users,
  userSettings,
  regionSettings,
  regionSettingsPerUser,
  regionMetadata,
  measurementSystems,
  basemaps,
  dateTimeFormats
}: RootState) => ({
  availableRegions: users.availableRegions,
  user: users.userClaims,
  regionSettings,
  regionSettingsPerUser,
  userSettings,
  regionMetadata,
  measurementSystems,
  basemaps,
  dateFormats: exists(dateTimeFormats) ? dateTimeFormats.dateFormats : [],
  timeFormats: exists(dateTimeFormats) ? dateTimeFormats.timeFormats : [],
  areDateTimeFormatsLoading: dateTimeFormats.loading,
  areBasemapsLoading: basemaps.loading,
  areMeasurementSystemsLoading: measurementSystems.loading,
  areRegionSettingsLoading: regionSettings.loading,
  doRegionSettingsExist: exists(regionSettings.id),
  areRegionSettingsPerUserLoading: regionSettingsPerUser.loading,
  doRegionSettingsPerUserExist: exists(regionSettingsPerUser.userId)
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getUserSettings: () => dispatch(getUserSettings()),
  getRegionSettings: () => dispatch(getRegionSettings()),
  getRegionSettingsPerUser: () => dispatch(getRegionSettingsPerUser()),
  getMeasurementSystems: () => dispatch(getMeasurementSystems()),
  getBasemaps: () => dispatch(getBasemaps()),
  getDateFormats: () => dispatch(getDateFormats()),
  getTimeFormats: () => dispatch(getTimeFormats()),
  updateRegionSettings: (values: any) => dispatch(updateRegionSettings(values)),
  updateRegionSettingsPerUser: (values: any) => dispatch(updateRegionSettingsPerUser(values)),
  canUpdateRegionSettings: () => dispatch(canPerformAction("region_settings", "update", "own"))
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type SettingsTabProps = {} & PropsFromRedux;

class SettingsTab extends React.Component<SettingsTabProps> {
  constructor(props: SettingsTabProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.props.getMeasurementSystems();
    this.props.getBasemaps();
    this.props.getUserSettings();
    this.props.getDateFormats();
    this.props.getTimeFormats();
    if (!this.props.areRegionSettingsLoading && !this.props.doRegionSettingsExist) {
      this.props.getRegionSettings();
    }
    if (!this.props.areRegionSettingsPerUserLoading && !this.props.doRegionSettingsPerUserExist) {
      this.props.getRegionSettingsPerUser();
    }
  }

  render() {
    const {
      areBasemapsLoading,
      areDateTimeFormatsLoading,
      areMeasurementSystemsLoading,
      availableRegions,
      basemaps,
      dateFormats,
      measurementSystems,
      regionMetadata,
      regionSettings,
      regionSettingsPerUser,
      timeFormats,
      user,
      userSettings
    } = this.props;

    const system = (user.selectedRoles as Role[])[0].system;
    return system === "urbancanvas" ? (
      <Grid columns="equal">
        <Grid.Column key="admin">
          <Administrator
            areBasemapsLoading={areBasemapsLoading}
            areDateTimeFormatsLoading={areDateTimeFormatsLoading}
            areMeasurementSystemsLoading={areMeasurementSystemsLoading}
            basemaps={basemaps.basemaps}
            canEdit={this.props.canUpdateRegionSettings()}
            dateFormats={dateFormats}
            measurementSystems={measurementSystems.measurementSystems}
            onSubmit={this.props.updateRegionSettings}
            regionMetadata={regionMetadata}
            regionSettings={regionSettings}
            timeFormats={timeFormats}
          />
        </Grid.Column>
        <Grid.Column key="user">
          <User
            areDateTimeFormatsLoading={areDateTimeFormatsLoading}
            areMeasurementSystemsLoading={areMeasurementSystemsLoading}
            availableRegions={availableRegions}
            dateFormats={dateFormats}
            defaultRegionId={userSettings.defaultRegionId}
            measurementSystems={measurementSystems.measurementSystems}
            onSubmit={this.props.updateRegionSettingsPerUser}
            regionSettings={regionSettingsPerUser}
            timeFormats={timeFormats}
          />
        </Grid.Column>
      </Grid>
    ) : (
      <Settings />
    );
  }
}

export default connector(SettingsTab);
